/**
 * Authentication Utilities
 */

// Production'da relative path kullan, development'ta localhost
const isProduction = import.meta.env.PROD || (typeof window !== 'undefined' && window.location.hostname !== 'localhost');
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || (isProduction ? '/api' : 'http://localhost:3000/api');

/**
 * Login
 */
export const login = async (email, password) => {
    try {
        const response = await fetch(`${API_BASE_URL}/auth/login`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({ email, password }),
            credentials: 'include',
            mode: 'cors'
        });

        // Check if response is ok
        if (!response.ok) {
            const errorText = await response.text();
            let errorData;
            try {
                errorData = JSON.parse(errorText);
            } catch {
                throw new Error(errorText || `HTTP ${response.status}: ${response.statusText}`);
            }
            throw new Error(errorData.message || errorData.error || 'Giriş başarısız');
        }

        const data = await response.json();

        // Store token (backend returns accessToken, we store it as token)
        if (data.data?.accessToken || data.data?.token) {
            const token = data.data.accessToken || data.data.token;
            const refreshToken = data.data.refreshToken || '';
            localStorage.setItem('token', token);
            localStorage.setItem('refreshToken', refreshToken);
            
            // Store user data if available
            if (data.data.user) {
                localStorage.setItem('user', JSON.stringify(data.data.user));
            }
        }

        return data;
    } catch (error) {
        console.error('Login error:', error);
        if (error.name === 'TypeError' && error.message.includes('fetch')) {
            throw new Error('Backend sunucusuna bağlanılamıyor. Lütfen backend\'in çalıştığından emin olun.');
        }
        throw error;
    }
};

/**
 * Register
 */
export const register = async (userData) => {
    const response = await fetch(`${API_BASE_URL}/auth/register`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json'
        },
        body: JSON.stringify(userData)
    });

    const data = await response.json();

    if (!response.ok) {
        throw new Error(data.message || 'Kayıt başarısız');
    }

    return data;
};

/**
 * Logout
 * @param {string} redirectTo - Çıkış sonrası yönlendirme (varsayılan: /admin/login)
 */
export const logout = async (redirectTo = '/admin/login') => {
    const token = localStorage.getItem('token');
    
    if (token) {
        try {
            await fetch(`${API_BASE_URL}/auth/logout`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });
        } catch (error) {
            console.error('Logout error:', error);
        }
    }

    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    window.location.href = redirectTo;
};

/**
 * Get current user
 */
export const getCurrentUser = async () => {
    const token = localStorage.getItem('token');
    
    if (!token) {
        return null;
    }

    try {
        const response = await fetch(`${API_BASE_URL}/auth/me`, {
            headers: {
                'Authorization': `Bearer ${token}`
            }
        });

        const data = await response.json();

        if (!response.ok) {
            throw new Error(data.message || 'Kullanıcı bilgisi alınamadı');
        }

        // Store user data
        const user = data.data?.user || data.data;
        if (user && (user.email || user.id)) {
            localStorage.setItem('user', JSON.stringify(user));
            return user;
        }
        // Fallback to stored user
        return getStoredUser();
    } catch (error) {
        console.error('Get current user error:', error);
        // Return stored user as fallback instead of logging out
        const storedUser = getStoredUser();
        if (!storedUser) {
            logout();
        }
        return storedUser;
    }
};

/**
 * Check if user is authenticated
 */
export const isAuthenticated = () => {
    return !!localStorage.getItem('token');
};

/**
 * Get stored user
 */
export const getStoredUser = () => {
    const userStr = localStorage.getItem('user');
    if (userStr) {
        try {
            const user = JSON.parse(userStr);
            // Ensure user has required fields
            if (user && (user.email || user.id)) {
                return user;
            }
        } catch {
            return null;
        }
    }
    return null;
};

/**
 * Refresh token
 */
export const refreshToken = async () => {
    const refreshToken = localStorage.getItem('refreshToken');
    
    if (!refreshToken) {
        throw new Error('Refresh token not found');
    }

    const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json'
        },
        body: JSON.stringify({ refreshToken })
    });

    const data = await response.json();

    if (!response.ok) {
        throw new Error(data.message || 'Token yenileme başarısız');
    }

    if (data.data?.token) {
        localStorage.setItem('token', data.data.token);
        if (data.data.refreshToken) {
            localStorage.setItem('refreshToken', data.data.refreshToken);
        }
    }

    return data;
};

